'use client';

// The dashboard's main navigation: the pinned rail in DashboardShell and, in
// its drawer variant, the phone hamburger drawer (BL-1439). Its parts live in
// ./sidebar/ (BL-2422 split this file): the header, the nav sections, the
// staff menu and the rail's collapse state.

import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import { clsx } from 'clsx';
import { Suspense, useState, useCallback } from 'react';
import ProfileModal from './settings/ProfileModal';
import UnsavedChangesDialog from './ui/UnsavedChangesDialog';
import { useGuardedNavigation } from '../hooks/useGuardedNavigation';
import { usePermissions } from '../hooks/usePermissions';
import TenantSelector from './tenant/TenantSelector';
import { useOfflineMode } from './offline/OfflineModeProvider';
import { navigation, type SidebarVariant } from './sidebar/nav-config';
import SidebarHeader from './sidebar/SidebarHeader';
import { SidebarNavSection } from './sidebar/SidebarNav';
import SidebarUserMenu from './sidebar/SidebarUserMenu';
import { useSidebarCollapse } from './sidebar/useSidebarCollapse';

interface SidebarContentProps {
  initialCollapsed: boolean;
  variant: SidebarVariant;
  /**
   * Ask the host to dismiss this Sidebar. Only meaningful in `drawer`, where
   * the host is a slide-out — fired after a soft navigation (AC 2) and by the
   * header's close control. The rail has no host to ask, and passes nothing.
   */
  onRequestClose?: () => void;
}

function SidebarContent({ initialCollapsed, variant, onRequestClose }: SidebarContentProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();
  const { canSeeNav } = usePermissions();
  const { isNativeApp } = useOfflineMode();
  // Called unconditionally — rules of hooks (`ui-standards.md` #7) — but its
  // result is IGNORED in the drawer, which is always expanded. The drawer
  // therefore never reaches `toggleCollapsed`, which is the only writer of the
  // collapse cookie: RAC 1 holds by construction rather than by care.
  const { isCollapsed: railCollapsed, toggleCollapsed } = useSidebarCollapse(initialCollapsed);
  const isDrawer = variant === 'drawer';
  const isCollapsed = isDrawer ? false : railCollapsed;
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  const handleRequestClose = useCallback(() => {
    onRequestClose?.();
  }, [onRequestClose]);

  const performNavigation = useCallback((href: string) => {
    // Entering /settings or /super-admin requires a FULL load, not a soft
    // router.push: the Classic vs v2 take-over shell is chosen server-side in
    // (dashboard)/layout.tsx by pathname. A soft transition keeps the shared
    // (dashboard) layout mounted, so a v2 user would see the page rendered inside
    // the app shell instead of the v2 take-over shell ("Settings doesn't load the
    // full new page"). Hard-navigate so the server layout re-runs and picks the
    // right shell. (v1 users get a harmless one-time reload entering the classic
    // page.)
    if (
      typeof window !== 'undefined' &&
      (href.startsWith('/settings') || href.startsWith('/super-admin'))
    ) {
      window.location.assign(href);
      return;
    }
    router.push(href);
    // BL-1439 (AC 2): a drawer item navigates AND closes the drawer. Fired here
    // rather than in `navigateSafely` on purpose — when the unsaved-changes
    // guard below intercepts, the drawer must STAY open behind the confirmation
    // it just raised. The dialog's confirm path routes back through this
    // function, so the close still happens once the operator says discard.
    //
    // The hard-navigation branch above returns early and needs no close: the
    // document is being replaced.
    onRequestClose?.();
  }, [router, onRequestClose]);

  // BL-758 (AC 10): router.push bypasses both beforeunload and anchor
  // interception, so a page holding unsaved edits flags the shared registry
  // and the sidebar asks before discarding them. BL-1669 lifted the
  // consult-and-hold into `useGuardedNavigation`, which the auction detail
  // tab strip shares — one mechanism for every programmatic navigator.
  const {
    navigate: navigateSafely,
    pending: pendingNavHref,
    confirmLeave,
    cancelLeave,
  } = useGuardedNavigation(performNavigation);

  return (
    <div
      // BUG-2294: names the rail's width for dialogs centring beside it (globals.css).
      data-shell-rail={isDrawer ? undefined : isCollapsed ? 'collapsed' : 'expanded'}
      className={clsx(
        'flex flex-col bg-sidebar',
        // The drawer fills the SlideOutPanel that hosts it — the panel owns the
        // width (`max-w-nav-drawer`) and the viewport-height positioning, and
        // this fill is also what hides the panel's own `bg-gray-50` without a
        // `clsx` background override (which would be unreliable: Tailwind
        // resolves conflicts by emission order, not class order — ADR 030's
        // BL-1004 hazard). The rail keeps its own height, border and width
        // transition; nothing here changes for it.
        isDrawer
          ? 'h-full w-full'
          : [
              // `dvh`, matching the shell frame this rail sits in. The two MUST
              // agree: `h-screen` inside an `h-dvh` parent makes the child taller
              // than its container by exactly the browser toolbar's height.
              'h-dvh border-r border-sidebar-border transition-[width] duration-200',
              isCollapsed ? 'w-rail-collapsed' : 'w-rail',
            ]
      )}
    >
      <SidebarHeader
        variant={variant}
        isCollapsed={isCollapsed}
        onToggle={isDrawer ? handleRequestClose : toggleCollapsed}
      />

      <TenantSelector collapsed={isCollapsed} />

      <nav
        data-testid="sidebar-nav"
        className={clsx(
          'flex-1 overflow-y-auto py-4',
          isCollapsed ? 'px-2 space-y-4' : 'px-3 space-y-6'
        )}
      >
        {navigation.map((section) => (
          <SidebarNavSection
            key={section.title}
            section={section}
            isCollapsed={isCollapsed}
            pathname={pathname}
            searchParams={searchParams}
            canSeeNav={canSeeNav}
            isNativeApp={isNativeApp}
            onNavigate={navigateSafely}
          />
        ))}
      </nav>

      <SidebarUserMenu
        isCollapsed={isCollapsed}
        onOpenProfile={() => setIsProfileModalOpen(true)}
      />

      <ProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
      />

      {/* BL-758 (AC 10): confirm before sidebar navigation discards another
          surface's unsaved edits. BL-914 AC 5: canonical copy binding — see
          UnsavedChangesDialog. */}
      <UnsavedChangesDialog
        isOpen={pendingNavHref !== null}
        onClose={cancelLeave}
        onConfirm={confirmLeave}
      />
    </div>
  );
}

interface SidebarProps {
  initialCollapsed?: boolean;
  /** Which surface this renders as — see {@link SidebarVariant}. Defaults to the rail. */
  variant?: SidebarVariant;
  /** Ask the host to dismiss the drawer. Ignored in the `rail` variant. */
  onRequestClose?: () => void;
}

export default function Sidebar({
  initialCollapsed = false,
  variant = 'rail',
  onRequestClose,
}: SidebarProps) {
  const isDrawer = variant === 'drawer';
  return (
    <Suspense
      fallback={
        <div
          data-shell-rail={isDrawer ? undefined : initialCollapsed ? 'collapsed' : 'expanded'}
          className={clsx(
            'flex items-center justify-center text-sidebar-text/80',
            // The drawer's host already sizes and positions the panel, so the
            // fallback fills it rather than re-stating a rail width.
            isDrawer ? 'h-full w-full' : ['h-dvh', initialCollapsed ? 'w-rail-collapsed' : 'w-rail']
          )}
        >
          {isDrawer || !initialCollapsed ? 'Loading...' : ''}
        </div>
      }
    >
      <SidebarContent
        initialCollapsed={initialCollapsed}
        variant={variant}
        onRequestClose={onRequestClose}
      />
    </Suspense>
  );
}
